// src/App.tsx
import React, { Suspense, lazy } from 'react';
import { Loader2 } from 'lucide-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Contextos
import { AuthProvider, useAuth, type SignUpPayload } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './modules/finance/components/ToastProvider';
import { useToast } from './modules/finance/components/ToastContext';

import { crmService } from './services/crmApi';
import { isCompanyModuleEnabled } from './utils/companyModules';

type MetaPixelArgument = string | number | boolean | null | Record<string, unknown>;
type MetaPixelFn = ((...args: MetaPixelArgument[]) => void) & {
  callMethod?: (...args: MetaPixelArgument[]) => void;
  queue?: MetaPixelArgument[][];
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: MetaPixelFn;
    _fbq?: MetaPixelFn;
  }
}

import {
  canAccessWhatsAppAdmin,
  canAccessWhatsAppAi,
  canAccessWhatsAppAiLegacy,
  canAccessWhatsAppAtendimento,
  canAccessWhatsAppGestao,
  getDefaultWhatsAppPath
} from './modules/chat/whatsappAccess';

// O Guarda-Costas (Bloqueia quem não tem empresa criada)
import CompanyGuard from './modules/finance/components/CompanyGuard';

// Error Boundary para evitar tela branca
import { ErrorBoundary } from './components/ErrorBoundary';

// Header/Banner global para avisos do SuperAdmin
import { GlobalBanner } from './components/GlobalBanner';
import { ImpersonateBanner } from './components/ImpersonateBanner';

const LoginView = lazy(() => import('./modules/auth/LoginView').then((module) => ({ default: module.LoginView })));
const FinanceCRM = lazy(() => import('./modules/finance/FinanceCRM'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const DocsLandingPage = lazy(() => import('./pages/DocsLandingPage'));
const PublicQuizTemplate = lazy(() => import('./pages/PublicQuizTemplate'));
const PublicPrivacyRequestPage = lazy(() => import('./pages/PublicPrivacyRequestPage'));
const PublicSelfServicePage = lazy(() => import('./pages/PublicSelfServicePage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const SupportView = lazy(() => import('./modules/support/SupportView').then((module) => ({ default: module.SupportView })));
const AdminSupportView = lazy(() => import('./modules/support/AdminSupportView').then((module) => ({ default: module.AdminSupportView })));
const WhatsAppInboxView = lazy(() => import('./modules/chat/WhatsAppInboxView').then((module) => ({ default: module.WhatsAppInboxView })));
const WhatsAppConfigView = lazy(() => import('./modules/chat/WhatsAppConfigView').then((module) => ({ default: module.WhatsAppConfigView })));
const WhatsAppManagementView = lazy(() => import('./modules/chat/WhatsAppManagementView').then((module) => ({ default: module.WhatsAppManagementView })));
const WhatsAppSettingsHubView = lazy(() => import('./modules/chat/WhatsAppSettingsHubView').then((module) => ({ default: module.WhatsAppSettingsHubView })));
const WhatsAppTriageBuilderView = lazy(() => import('./modules/chat/WhatsAppTriageBuilderView').then((module) => ({ default: module.WhatsAppTriageBuilderView })));
const WhatsAppAiConfigView = lazy(() => import('./modules/chat/WhatsAppAiConfigView').then((module) => ({ default: module.WhatsAppAiConfigView })));
const WhatsAppAiReviewsView = lazy(() => import('./modules/chat/WhatsAppAiReviewsView').then((module) => ({ default: module.WhatsAppAiReviewsView })));
const WhatsAppAiLegacyExamplesView = lazy(() => import('./modules/chat/WhatsAppAiLegacyExamplesView').then((module) => ({ default: module.WhatsAppAiLegacyExamplesView })));
const ChatLayout = lazy(() => import('./modules/chat/layouts/ChatLayout').then((module) => ({ default: module.ChatLayout })));
const MarketingLayout = lazy(() => import('./modules/marketing/layouts/MarketingLayout').then((module) => ({ default: module.MarketingLayout })));
const CampaignView = lazy(() => import('./modules/marketing/views/CampaignView').then((module) => ({ default: module.CampaignView })));
const QuizzesView = lazy(() => import('./modules/marketing/views/QuizzesView').then((module) => ({ default: module.QuizzesView })));

const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="flex flex-col items-center gap-3">
      <Loader2 size={36} className="animate-spin text-indigo-500" />
      <p className="text-sm font-medium text-slate-400">Carregando módulo...</p>
    </div>
  </div>
);

const APP_HOSTNAMES = new Set([
  'app.vendaprocrm.com.br'
]);

const DOCS_HOSTNAMES = new Set([
  'docs.vendaprocrm.com.br'
]);

const STATUS_HOSTNAMES = new Set([
  'status.vendaprocrm.com.br'
]);

type PublicHostMode = 'site' | 'app' | 'docs' | 'status';

function getPublicHostMode(): PublicHostMode {
  if (typeof window === 'undefined') {
    return 'site';
  }

  const hostname = String(window.location.hostname || '').trim().toLowerCase();

  if (APP_HOSTNAMES.has(hostname)) {
    return 'app';
  }

  if (DOCS_HOSTNAMES.has(hostname)) {
    return 'docs';
  }

  if (STATUS_HOSTNAMES.has(hostname)) {
    return 'status';
  }

  return 'site';
}

function ensureMetaPixel(pixelId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof window.fbq !== 'function') {
    const fbq = ((...args: MetaPixelArgument[]) => {
      if (typeof fbq.callMethod === 'function') {
        fbq.callMethod(...args);
        return;
      }

      fbq.queue?.push(args);
    }) as MetaPixelFn;

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    window.fbq = fbq;
    window._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }

  window.fbq?.('init', pixelId);
  window.fbq?.('track', 'PageView');
}

const AppRoutes = () => {
  const { signed, loading, authLoading, signIn, signUp, user } = useAuth();
  const { addToast } = useToast();

  // Constante para definir quem é o admin do suporte
  const ADMIN_EMAIL = (import.meta.env.VITE_ADMIN_EMAIL || 'sistemas@rbfinanceira.com.br').trim().toLowerCase();
  const role = (user?.role || '').trim().toLowerCase();
  const cleanEmail = (user?.email || '').trim().toLowerCase();

  // 🚨 CORREÇÃO: Apenas SuperAdmin ou o email mestre podem ver o painel de suporte
  // Administradores de empresas devem ver a tela de abrir chamados (SupportView)
  const isSuperAdmin = cleanEmail === ADMIN_EMAIL || role === 'superadmin';
  const hasCompanyModule = (moduleKey: string) => {
    return isCompanyModuleEnabled(user, moduleKey);
  };
  const publicHostMode = getPublicHostMode();
  const shouldUseAppRootRedirect = publicHostMode === 'app';
  const shouldRenderDocsRoot = publicHostMode === 'docs';
  const shouldRenderStatusRoot = publicHostMode === 'status';

  // Injeção Global do Meta Pixel
  React.useEffect(() => {
    const pathname = typeof window === 'undefined' ? '' : String(window.location.pathname || '');
    const isCompanyPublicFunnel = pathname.startsWith('/contratar/') || pathname.startsWith('/privacidade/');

    if (shouldRenderDocsRoot || shouldRenderStatusRoot || isCompanyPublicFunnel) {
      return;
    }

    crmService.getPublicSaasConfig().then((config) => {
      const pixelId = config?.metaPixelId;
      if (pixelId) {
        ensureMetaPixel(pixelId);
      }
    }).catch(console.error);
  }, [shouldRenderDocsRoot, shouldRenderStatusRoot]);


  const handleLogin = async (email: string, password?: string) => {
    await signIn(email, password || '');
  };

  const handleRegister = async (data: SignUpPayload) => {
    if (signUp) {
      await signUp(data);
    } else {
      console.error('Função de cadastro (signUp) não encontrada no AuthContext.');
      addToast('Cadastro indisponível no momento. Tente novamente em instantes.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={40} className="text-blue-600 animate-spin" />
          <p className="text-slate-400 text-sm font-medium">Carregando sistema...</p>
        </div>
      </div>
    );
  }

  if (shouldRenderDocsRoot) {
    return (
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/" element={<DocsLandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (shouldRenderStatusRoot) {
    return (
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/" element={<StatusPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  const renderWhatsAppRoute = (requiredSection: 'atendimento' | 'config' | 'gestao' | 'ia' | 'ai_reviews' | 'ai_legacy' | 'admin', element: React.ReactElement) => {
    if (!signed) {
      return <Navigate to="/login" />;
    }

    let hasAccess = false;
    if (requiredSection === 'atendimento') {
      hasAccess = canAccessWhatsAppAtendimento(user);
    } else if (requiredSection === 'config') {
      hasAccess = canAccessWhatsAppGestao(user);
    } else if (requiredSection === 'gestao') {
      hasAccess = canAccessWhatsAppGestao(user);
    } else if (requiredSection === 'ia' || requiredSection === 'ai_reviews') {
      hasAccess = canAccessWhatsAppAi(user);
    } else if (requiredSection === 'ai_legacy') {
      hasAccess = canAccessWhatsAppAiLegacy(user);
    } else {
      hasAccess = canAccessWhatsAppAdmin(user);
    }

    if (!hasAccess) {
      return <Navigate to={getDefaultWhatsAppPath(user)} replace />;
    }

    return (
      <CompanyGuard>
        {element}
      </CompanyGuard>
    );
  };

  const renderMarketingRoute = (element: React.ReactElement) => {
    if (!signed) {
      return <Navigate to="/login" />;
    }

    if (!hasCompanyModule('marketing')) {
      return <Navigate to="/dashboard" replace />;
    }

    return (
      <CompanyGuard>
        {element}
      </CompanyGuard>
    );
  };

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
      {/* --- ROTAS PÚBLICAS --- */}
      <Route
        path="/"
        element={shouldUseAppRootRedirect ? (
          <Navigate to={signed ? "/dashboard" : "/login"} replace />
        ) : (
          <LandingPage />
        )}
      />
      <Route path="/privacidade/:empresaSlug" element={<PublicPrivacyRequestPage />} />
      <Route path="/contratar/:empresaSlug" element={<PublicSelfServicePage />} />
      <Route path="/:empresaSlug/:quizSlug" element={<PublicQuizTemplate />} />

      <Route
        path="/login"
        element={
          signed ? <Navigate to="/dashboard" /> : (
            <LoginView
              onLogin={handleLogin}
              onRegister={handleRegister}
              loading={authLoading}
            />
          )
        }
      />

      {/* --- ROTAS PRIVADAS --- */}

      {/* 1. Dashboard (Financeiro) */}
      <Route
        path="/dashboard"
        element={
          signed ? (
            <CompanyGuard>
              <FinanceCRM />
            </CompanyGuard>
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* 2. Rota de Suporte Inteligente */}
      <Route
        path="/suporte"
        element={
          !signed ? (
            <Navigate to="/login" />
          ) : isSuperAdmin ? (
            // ✅ SE FOR SUPER ADMIN: Mostra o Painel de Atendimento
            <AdminSupportView />
          ) : (
            // ✅ SE FOR ADMIN DE EMPRESA OU USER: Mostra a Tela de Chamados
            <CompanyGuard>
              <SupportView />
            </CompanyGuard>
          )
        }
      />

      {/* 3. Módulo de Atendimento Omnichannel (WhatsApp) */}
      <Route element={<ChatLayout />}>
        <Route
          path="/chat"
          element={<Navigate to={getDefaultWhatsAppPath(user)} replace />}
        />
        <Route
          path="/chat/atendimento"
          element={renderWhatsAppRoute('atendimento', <WhatsAppInboxView />)}
        />
        <Route
          path="/chat/instagram"
          element={renderWhatsAppRoute('atendimento', <WhatsAppInboxView />)}
        />
        <Route
          path="/chat/messenger"
          element={renderWhatsAppRoute('atendimento', <WhatsAppInboxView />)}
        />
        <Route
          path="/chat/config"
          element={renderWhatsAppRoute('config', <WhatsAppSettingsHubView />)}
        />
        <Route
          path="/chat/gestao"
          element={renderWhatsAppRoute('gestao', <WhatsAppManagementView />)}
        />
        <Route
          path="/chat/gestao/triagem"
          element={renderWhatsAppRoute('gestao', <WhatsAppTriageBuilderView />)}
        />
        <Route
          path="/chat/ia"
          element={renderWhatsAppRoute('ia', <WhatsAppAiConfigView />)}
        />
        <Route
          path="/chat/ia/reviews"
          element={renderWhatsAppRoute('ai_reviews', <WhatsAppAiReviewsView />)}
        />
        <Route
          path="/chat/ia/legacy"
          element={renderWhatsAppRoute('ai_legacy', <WhatsAppAiLegacyExamplesView />)}
        />
        <Route
          path="/chat/admin"
          element={renderWhatsAppRoute('admin', <WhatsAppConfigView />)}
        />
        <Route path="/chat/configuracoes" element={<Navigate to="/chat/config" replace />} />
      </Route>

      {/* 4. Módulo de Marketing (Disparador Integrado) */}
      <Route element={<MarketingLayout />}>
        <Route
          path="/marketing/conexoes"
          element={
            signed ? (
              <Navigate to="/chat/admin" replace />
            ) : <Navigate to="/login" />
          }
        />
        <Route
          path="/marketing/campanhas"
          element={renderMarketingRoute(<CampaignView />)}
        />
        <Route
          path="/marketing/quizzes"
          element={renderMarketingRoute(<QuizzesView />)}
        />
      </Route>

      {/* Rota coringa (Redireciona erros para a home) */}
      <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          {/* ✅ O ToastProvider envolve as rotas para funcionar em todo o app */}
          <ToastProvider>
            <ErrorBoundary>
              <ImpersonateBanner />
              <GlobalBanner />
              <AppRoutes />
            </ErrorBoundary>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
